/**
 * Paper — one 8.5" × 11" letter sheet with the OJB template's wide-left-margin
 * layout. Renders the floated logo and office-locations ribbon in the left
 * margin strip; slot for page content in the right column.
 *
 * Geometry matches the docx template exactly so screen and print stay in sync:
 *   Paper:       8.5in × 11in
 *   Top margin:  1.00in
 *   Right:       0.70in
 *   Bottom:      1.51in
 *   Left:        2.85in
 *   Content:     5.00in × 8.49in (usable)
 */
import type { ReactNode } from 'react';
import { OJB_OFFICES, OJB_URL_LABEL } from '@ojb/shared';

interface Props {
  children: ReactNode;
  pageNumber?: number;
  totalPages?: number;
  /** Hide the OJB logo + office ribbon (useful for continuation pages in some styles) */
  hideMarginDecoration?: boolean;
  /** When false, the bottom-right page number is hidden even on
   *  multi-page documents. Default true. Affects export too —
   *  the .docx + .pdf paths read this same flag. */
  showPageNumber?: boolean;
}

export function Paper({
  children,
  pageNumber,
  totalPages,
  hideMarginDecoration,
  showPageNumber = true,
}: Props) {
  return (
    <article
      className="relative mx-auto bg-white text-charcoal shadow-[0_2px_16px_rgba(0,0,0,0.15)] ring-1 ring-black/5"
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        width: '8.5in',
        height: '11in',
        paddingTop: '1in',
        paddingRight: '0.7in',
        paddingBottom: '1.51in',
        paddingLeft: '2.85in',
        overflow: 'hidden',
      }}
    >
      {!hideMarginDecoration && (
        <>
          {/* OJB wordmark (real brand asset) floated in top-left margin strip */}
          <img
            src="/ojb-logo-charcoal.png"
            alt="OJB"
            aria-hidden
            style={{
              position: 'absolute',
              top: '0.5in',
              left: '0.7in',
              width: '1.3in',
              height: 'auto',
            }}
          />

          {/* Office-locations ribbon floated in lower-left margin */}
          <div
            className="absolute"
            style={{
              left: '0.7in',
              bottom: '0.8in',
              width: '1.5in',
              borderTop: '0.5pt solid #9EA1A2',
              borderBottom: '0.5pt solid #9EA1A2',
              padding: '8pt 0',
            }}
            aria-hidden
          >
            <div className="mb-1.5 text-[7pt] italic tracking-wider text-pine">
              {OJB_URL_LABEL}
            </div>
            <ul className="space-y-1">
              {OJB_OFFICES.map((o) => (
                <li key={o.city}>
                  <div className="text-[8pt] font-medium tracking-wider text-pine">
                    {o.city}
                  </div>
                  <div className="text-[6pt] italic tracking-wider text-pine/70">
                    {o.state}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Page number (bottom-right in the main content column).
          Honors the showPageNumber toggle even on single-page docs
          so the affordance is consistent — toggle on = always
          visible, toggle off = always hidden. */}
      {showPageNumber && pageNumber !== undefined && totalPages !== undefined && (
        <div
          className="absolute text-[8pt] text-smoke"
          style={{ right: '0.7in', bottom: '0.5in' }}
        >
          {pageNumber} / {totalPages}
        </div>
      )}

      {/* Main content column */}
      <div className="relative h-full">{children}</div>
    </article>
  );
}
